import { useMemo, useState } from 'react';
import { getYear } from 'date-fns';
import { useVacationDpBoard } from './useVacationDpBoard';
import { findTeamVacationOverlaps, hasPendingConflict } from '../utils/vacationTeamOverlap';
import type { ClientVacationGroup, ClientVacationPeriod } from '../models/vacationClientView.types';

export function useVacationClientBoard() {
    const { rows } = useVacationDpBoard();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [showOnlyOverlaps, setShowOnlyOverlaps] = useState(false);

    const allGroups = useMemo(() => {
        return findTeamVacationOverlaps(rows);
    }, [rows]);

    const clientGroups = useMemo(() => {
        const monthStart = selectedMonth ? new Date(selectedYear, selectedMonth - 1, 1) : null;
        const monthEnd = selectedMonth ? new Date(selectedYear, selectedMonth, 0) : null;

        const filtered = allGroups.map((group) => {
            const responsibles = group.responsibles.map((responsible) => {
                let periods = responsible.periods.filter((p) => {
                    if (monthStart && monthEnd) {
                        return p.startDate <= monthEnd && p.endDate >= monthStart;
                    }
                    const startYear = getYear(p.startDate);
                    const endYear = getYear(p.endDate);
                    return startYear <= selectedYear && endYear >= selectedYear;
                });

                if (showOnlyOverlaps) {
                    periods = periods.filter(hasPendingConflict);
                }

                if (periods.length === 0) return null;

                const totalPeople = new Set(periods.map(p => p.collaboratorId)).size;
                const overlapCount = periods.filter(hasPendingConflict).length;
                const pendingCount = periods.filter(p => p.isPending).length;

                return {
                    ...responsible,
                    periods,
                    totalPeople,
                    overlapCount,
                    pendingCount,
                };
            }).filter(Boolean) as any[];

            if (responsibles.length === 0) return null;

            const allDistinctPeople = new Set(
                responsibles.flatMap(r => r.periods.map((period: ClientVacationPeriod) => period.collaboratorId))
            ).size;
            const allOverlapCount = responsibles.reduce((sum, r) => sum + r.overlapCount, 0);
            const allPendingCount = responsibles.reduce((sum, r) => sum + r.pendingCount, 0);

            return {
                ...group,
                responsibles,
                totalPeople: allDistinctPeople,
                overlapCount: allOverlapCount,
                pendingCount: allPendingCount,
            };
        }).filter(Boolean) as ClientVacationGroup[];

        return filtered;
    }, [allGroups, selectedYear, selectedMonth, showOnlyOverlaps]);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = new Set<number>([currentYear]);
        allGroups.forEach((group) =>
            group.responsibles.forEach((responsible) =>
                responsible.periods.forEach((p) => {
                    for (let year = Math.max(getYear(p.startDate), currentYear); year <= getYear(p.endDate); year++) {
                        years.add(year);
                    }
                })
            )
        );
        return Array.from(years).sort((a, b) => a - b);
    }, [allGroups]);

    const summary = useMemo(() => {
        const clientCount = clientGroups.length;
        const peopleCount = new Set(
            clientGroups.flatMap((g) => g.responsibles.flatMap((r) => r.periods.map((period) => period.collaboratorId)))
        ).size;
        const overlapCount = clientGroups.reduce((sum, g) => sum + g.overlapCount, 0);

        return { clientCount, peopleCount, overlapCount };
    }, [clientGroups]);

    return {
        clientGroups,
        rows,
        selectedYear,
        setSelectedYear,
        selectedMonth,
        setSelectedMonth,
        yearOptions,
        showOnlyOverlaps,
        setShowOnlyOverlaps,
        summary,
    };
}
